import { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAssistant } from "@/context/AssistantContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteSettings } from "@/hooks/use-site-settings";
import ReactMarkdown from "react-markdown";

type Msg = { role: "user" | "assistant"; content: string };

const SUGGESTIONS_LV: Record<string, string[]> = {
  rackets: [
    "Vai šī rakete derēs manam spēles stilam?",
    "Kādam spēlētājam šī ir vislabāk piemērota?",
    "Iesācējam, vidēja līmeņa vai pieredzējušam?",
    "Vairāk jaudai vai kontrolei?",
  ],
  shoes: [
    "Vai tie ir labi ātrām, agresīvām kustībām?",
    "Vairāk komfortam vai ātrumam?",
    "Kādam laukuma segumam tie ir paredzēti?",
    "Vai der platām pēdām?",
  ],
  bags: [
    "Vai iederēsies viss mans inventārs?",
    "Cik raketes ietilpst iekšā?",
    "Vai der turnīriem?",
  ],
  balls: [
    "Labākas treniņiem vai mačiem?",
    "Cik izturīgas tās ir?",
  ],
  apparel: [
    "Sporta vai brīvāks piegriezums?",
    "Vai der siltam laikam?",
  ],
  accessories: [
    "Vairāk gripam, komfortam vai sviedru uzsūkšanai?",
  ],
};

const SUGGESTIONS_EN: Record<string, string[]> = {
  rackets: [
    "Will this racket suit my playing style?",
    "What type of player is this best for?",
    "Beginner, intermediate or advanced?",
    "Power or control?",
  ],
  shoes: [
    "Good for fast, aggressive movement?",
    "Better for comfort or speed?",
    "What court surface is this for?",
    "Good for wide feet?",
  ],
  bags: [
    "Will all my gear fit?",
    "How many rackets fit inside?",
    "Good for tournaments?",
  ],
  balls: [
    "Better for training or matches?",
    "How durable are they?",
  ],
  apparel: [
    "Athletic fit or relaxed fit?",
    "Good for warm weather?",
  ],
  accessories: [
    "Better for grip, comfort or sweat absorption?",
  ],
};

const GENERIC_LV = [
  "Iesaki raketi iesācējam zem €150",
  "Kur atrodu apavus?",
  "Kāda ir piegādes maksa?",
  "Kā darbojas atgriešana?",
];
const GENERIC_EN = [
  "Recommend a control racket under €150",
  "Where can I find shoes?",
  "What is the shipping cost?",
  "How do returns work?",
];

function normalizeType(t?: string): string {
  if (!t) return "";
  const v = t.toLowerCase();
  if (v.startsWith("racket")) return "rackets";
  if (v.startsWith("shoe")) return "shoes";
  if (v.startsWith("bag")) return "bags";
  if (v.startsWith("ball")) return "balls";
  if (v.startsWith("apparel") || v.startsWith("apģērb")) return "apparel";
  if (v.startsWith("accessor") || v.startsWith("aksesuār")) return "accessories";
  return v;
}

export default function AiAssistant() {
  const { product, open, setOpen, consumePendingQuery } = useAssistant();
  const { lang } = useLanguage();
  const { data: siteSettings } = useSiteSettings();
  const lv = lang === "lv";
  const aiEnabled = siteSettings?.ai_assistant_enabled !== "false";

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const productType = normalizeType(product?.product_type);
  const suggestions = product
    ? (lv ? SUGGESTIONS_LV : SUGGESTIONS_EN)[productType] || (lv ? GENERIC_LV : GENERIC_EN)
    : (lv ? GENERIC_LV : GENERIC_EN);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading || !aiEnabled) return;
    const next: Msg[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/ai-assistant`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next, productContext: product }),
      });

      if (!res.ok || !res.body) {
        const errBody = await res.json().catch(() => ({ error: "Network error" }));
        setMessages((m) => [
          ...m,
          { role: "assistant", content: errBody?.error || (lv ? "Radās kļūda. Mēģini vēlreiz." : "Something went wrong. Please try again.") },
        ]);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      setMessages((m) => [...m, { role: "assistant", content: "" }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = m.slice();
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e: any) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: lv ? "Nevarēju sazvanīt asistentu. Mēģini vēlreiz." : "Couldn't reach the assistant. Try again." },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [messages, loading, product, lv]);

  // Handle pending query (e.g. coming from a triggered prompt)
  useEffect(() => {
    if (!open) return;
    const q = consumePendingQuery();
    if (q) void send(q);
    // focus input on open
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [open, consumePendingQuery, send]);

  // Mirror WhatsAppButton's scroll-aware lift so AI sits cleanly above WA on mobile
  const [scrollLift, setScrollLift] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrollLift(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Animated mount/unmount for premium open/close transition
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Double rAF guarantees the browser paints the initial (closed) styles
      // before flipping to visible, so the transform/opacity transition actually runs.
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 260);
      return () => clearTimeout(t);
    }
  }, [open]);

  return (
    <>
      {/* Floating launcher — same 40px circular size as WhatsApp, sits above it */}
      <button
        onClick={() => setOpen(true)}
        aria-label={lv ? "Atvērt AI konsultantu" : "Open AI assistant"}
        aria-hidden={open}
        tabIndex={open ? -1 : 0}
        className={`fixed right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 ease-in-out
          bg-primary hover:bg-primary/90 text-primary-foreground
          md:bottom-[5.5rem]
          ${scrollLift ? "bottom-[8rem]" : "bottom-[4.5rem]"}
          ${open ? "opacity-0 scale-75 pointer-events-none" : "opacity-100 scale-100"}
        `}
      >
        <Sparkles className="w-5 h-5" />
      </button>

      {/* Chat panel */}
      {mounted && (
        <div
          className="fixed inset-x-0 bottom-0 md:inset-auto md:bottom-6 md:right-4 z-[70] w-full md:w-[400px] md:max-w-[calc(100vw-2rem)] origin-bottom-right will-change-transform"
          style={{
            transition: "opacity 280ms, transform 280ms",
            transitionTimingFunction: visible ? "cubic-bezier(0.16, 1, 0.3, 1)" : "cubic-bezier(0.4, 0, 1, 1)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.9)",
            pointerEvents: visible ? "auto" : "none",
          }}
        >
          <div className="bg-background border border-border md:rounded-2xl shadow-2xl flex flex-col h-[85vh] md:h-[600px] md:max-h-[calc(100vh-6rem)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-primary text-primary-foreground">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-sm leading-tight">
                    {lv ? "PADELCORNER.LV AI Konsultants" : "PADELCORNER.LV AI Assistant"}
                  </div>
                  <div className="text-xs opacity-80">
                    {lv ? "Tavs padel eksperts 24/7" : "Your padel expert 24/7"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-1 rounded hover:bg-primary-foreground/15"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Product context badge */}
            {product && (
              <div className="px-4 py-2 border-b border-border bg-muted/40">
                <Badge variant="secondary" className="text-[11px]">
                  {lv ? "Skatāmais produkts" : "Viewing"}: {product.name}
                </Badge>
              </div>
            )}

            {!aiEnabled ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center">
                <div className="space-y-3 max-w-sm">
                  <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-foreground font-medium">
                    {lv
                      ? "AI Konsultants pašlaik nav pieejams un vēl nav sācis darbu. Tas tev palīdzēs, tiklīdz tas būs iespējams."
                      : "AI Consultant is currently unavailable and has not started work yet. It will assist you as soon as possible."}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        {lv
                          ? "Sveiks! Es palīdzēšu izvēlēties pareizo padel inventāru un atbildēšu uz jautājumiem par veikalu."
                          : "Hi! I'll help you choose the right padel gear and answer any store question."}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((s) => (
                          <button
                            key={s}
                            onClick={() => send(s)}
                            className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent transition-colors text-left"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={
                          m.role === "user"
                            ? "max-w-[85%] rounded-2xl px-3 py-2 bg-primary text-primary-foreground text-sm whitespace-pre-wrap"
                            : "max-w-[90%] text-sm text-foreground"
                        }
                      >
                        {m.role === "assistant" ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none prose-a:text-primary prose-a:underline prose-p:my-1 prose-ul:my-1">
                            <ReactMarkdown
                              components={{
                                a: ({ href, children }) => (
                                  <a
                                    href={href}
                                    onClick={(e) => {
                                      if (href?.startsWith("/")) {
                                        e.preventDefault();
                                        setOpen(false);
                                        window.location.href = href;
                                      }
                                    }}
                                  >
                                    {children}
                                  </a>
                                ),
                              }}
                            >
                              {m.content || (loading && i === messages.length - 1 ? "…" : "")}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          m.content
                        )}
                      </div>
                    </div>
                  ))}

                  {loading && messages[messages.length - 1]?.role === "user" && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {lv ? "Domāju…" : "Thinking…"}
                    </div>
                  )}
                </div>

                {/* Quick suggestions inline (when there are messages) */}
                {messages.length > 0 && !loading && (
                  <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                    {suggestions.slice(0, 3).map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-[11px] px-2.5 py-1 rounded-full border border-border hover:bg-accent transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send(input);
                  }}
                  className="border-t border-border p-3 flex items-end gap-2"
                >
                  <Textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send(input);
                      }
                    }}
                    placeholder={lv ? "Uzdod jautājumu…" : "Ask a question…"}
                    rows={1}
                    className="resize-none min-h-[40px] max-h-32 text-sm"
                    disabled={loading}
                  />
                  <Button type="submit" size="icon" disabled={loading || !input.trim()}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
